import type { PostgresOptions } from '#layers/data-model/data-connectors/postgres';
import type { BrandedConnectorOptions } from '#layers/data-model/utils/data-connectors/branded';
import postgres from '#layers/data-model/data-connectors/postgres';
import { AuthType } from '#layers/data-model/utils/data-connectors/aws-iam';
import { createBrandedConnector } from '#layers/data-model/utils/data-connectors/branded';

const brands: BrandedConnectorOptions<PostgresOptions>[] = [
	{
		id: 'supabase',
		name: '$t:connectors.supabase.name',
		description: '$t:connectors.supabase.description',
		icon: 'simple-icons:supabase',
		iconBackground: '#3ECF8E',
		iconForeground: '#1C1C1C',
		keywords: ['supabase', 'cloud', 'hosted', 'baas'],
		availableSince: '2026-01-01',
		overrides: {
			connectionStringPlaceholder: 'postgresql://postgres:[YOUR-PASSWORD]@db.[WORKSPACE-REF].supabase.co:5432/postgres',
		},
		notices: [
			{ color: 'neutral', title: '$t:connectors.supabase.dashboard_notice' },
			{
				color: 'neutral',
				title: '$t:connectors.supabase.password_placeholder_warning',
				when: (values) => values.password?.includes('[YOUR-PASSWORD]') ?? false,
			},
		],
	},
	{
		id: 'neon',
		name: '$t:connectors.neon.name',
		description: '$t:connectors.neon.description',
		icon: 'simple-icons:neon',
		iconBackground: '#34D59A',
		iconForeground: '#1C1C1C',
		keywords: ['neon', 'neon postgres', 'neondatabase', 'serverless postgres', 'databricks neon'],
		availableSince: '2026-06-16',
		docsUrl: 'https://neon.com/docs/connect/connect-from-any-app',
		overrides: {
			connectionStringPlaceholder: 'postgresql://alex:AbC123dEf@ep-cool-darkness-a1b2c3d4-pooler.us-east-2.aws.neon.tech/dbname?sslmode=require&channel_binding=require',
		},
		notices: [
			{ color: 'neutral', title: '$t:connectors.neon.setup_notice' },
		],
	},
	{
		id: 'planetscale-postgres',
		name: '$t:connectors.planetscale-postgres.name',
		description: '$t:connectors.planetscale-postgres.description',
		icon: 'simple-icons:planetscale',
		iconBackground: '#000000',
		iconForeground: '#FFFFFF',
		keywords: ['planetscale', 'planet scale', 'planetscale postgres', 'serverless postgres'],
		availableSince: '2026-06-16',
		docsUrl: 'https://planetscale.com/docs/postgres/connecting',
		overrides: {
			connectionStringPlaceholder: 'postgresql://user:password@host.region.psdb.cloud:5432/dbname',
		},
	},
	{
		id: 'alloydb',
		name: '$t:connectors.alloydb.name',
		description: '$t:connectors.alloydb.description',
		icon: 'connectors:alloydb',
		iconBackground: '#4285F4',
		iconForeground: '#FFFFFF',
		keywords: ['alloydb', 'google alloydb', 'google cloud alloydb', 'gcp alloydb'],
		availableSince: '2026-06-16',
		docsUrl: 'https://docs.cloud.google.com/alloydb/docs/connection-overview',
		overrides: {
			connectionStringPlaceholder: 'postgresql://postgres:password@<ALLOYDB_IP>:5432/postgres',
		},
		notices: [
			{ color: 'neutral', title: '$t:connectors.alloydb.setup_notice' },
		],
	},
	{
		id: 'heroku-postgres',
		name: '$t:connectors.heroku-postgres.name',
		description: '$t:connectors.heroku-postgres.description',
		icon: 'connectors:heroku',
		iconBackground: '#5A1BA9',
		iconForeground: '#FFFFFF',
		keywords: ['heroku', 'heroku postgres', 'heroku postgresql', 'salesforce heroku'],
		availableSince: '2026-06-16',
		docsUrl: 'https://devcenter.heroku.com/articles/connecting-heroku-postgres',
		overrides: {
			connectionStringPlaceholder: 'postgres://u42424246psem9:password@ec2-52-19-101-86.eu-west-1.compute.amazonaws.com:5432/d9fm5ro65cprs0',
		},
	},
	{
		id: 'crunchy-bridge',
		name: '$t:connectors.crunchy-bridge.name',
		description: '$t:connectors.crunchy-bridge.description',
		icon: 'connectors:crunchy-bridge',
		iconBackground: '#3A6AE8',
		iconForeground: '#FFFFFF',
		keywords: ['crunchy bridge', 'crunchy data', 'crunchydata', 'crunchy postgres', 'postgresbridge'],
		availableSince: '2026-06-16',
		docsUrl: 'https://docs.crunchybridge.com/connecting',
		overrides: {
			connectionStringPlaceholder: 'postgres://application:password@p.vbjrfujv5beutlw725gvi3i.db.postgresbridge.com:5432/postgres?sslmode=verify-full&sslrootcert=/path/to/Our_team.pem',
		},
	},
	{
		id: 'edb-postgres',
		name: '$t:connectors.edb-postgres.name',
		description: '$t:connectors.edb-postgres.description',
		icon: 'simple-icons:enterprisedb',
		iconBackground: '#FF3E00',
		iconForeground: '#FFFFFF',
		keywords: ['edb', 'enterprisedb', 'biganimal', 'edb postgres', 'edb postgres ai', 'oracle compatible postgres'],
		availableSince: '2026-06-16',
		docsUrl: 'https://www.enterprisedb.com/docs/biganimal/latest/using_cluster/02_connecting_your_cluster/connecting_from_a_client/',
		overrides: {
			connectionStringPlaceholder: 'postgres://edb_admin@p-xxxxxxxxxxx.pg.biganimal.io:5432/edb_admin?sslmode=require',
		},
	},
	{
		id: 'yugabytedb',
		name: '$t:connectors.yugabytedb.name',
		description: '$t:connectors.yugabytedb.description',
		icon: 'connectors:yugabytedb',
		iconBackground: '#FF6E42',
		iconForeground: '#FFFFFF',
		keywords: ['yugabyte', 'yugabytedb', 'ysql', 'distributed postgres', 'distributed sql'],
		availableSince: '2026-06-16',
		supportLevel: 'alpha',
		docsUrl: 'https://docs.yugabyte.com/stable/develop/drivers-orms/nodejs/postgres-pg-reference/',
		overrides: {
			connectionStringPlaceholder: 'postgresql://yugabyte:<password>@<host>:5433/yugabyte',
			defaultPort: 5433,
		},
	},
	{
		id: 'timescaledb',
		name: '$t:connectors.timescaledb.name',
		description: '$t:connectors.timescaledb.description',
		icon: 'simple-icons:timescale',
		iconBackground: '#FDB515',
		iconForeground: '#1C1C1C',
		keywords: ['timescale', 'timescaledb', 'time-series', 'tsdb', 'tigerdata'],
		availableSince: '2026-06-16',
		docsUrl: 'https://www.tigerdata.com/docs/self-hosted/latest/install/',
		overrides: {
			connectionStringPlaceholder: 'postgres://tsdbadmin:<password>@<host>.tsdb.cloud.timescale.com:<port>/tsdb?sslmode=require',
		},
	},
	{
		id: 'aiven-postgres',
		name: '$t:connectors.aiven-postgres.name',
		description: '$t:connectors.aiven-postgres.description',
		icon: 'connectors:aiven',
		iconBackground: '#FF5A00',
		iconForeground: '#FFFFFF',
		keywords: ['aiven', 'aiven postgres', 'aiven postgresql', 'managed postgres'],
		availableSince: '2026-06-16',
		docsUrl: 'https://aiven.io/docs/products/postgresql/get-started',
		overrides: {
			connectionStringPlaceholder: 'postgres://<user>:<password>@<service>.a.aivencloud.com:<port>/defaultdb?sslmode=require',
		},
	},
	{
		id: 'digitalocean-postgres',
		name: '$t:connectors.digitalocean-postgres.name',
		description: '$t:connectors.digitalocean-postgres.description',
		icon: 'simple-icons:digitalocean',
		iconBackground: '#0080FF',
		iconForeground: '#FFFFFF',
		keywords: ['digitalocean', 'digital ocean', 'do postgres', 'do managed database'],
		availableSince: '2026-06-16',
		docsUrl: 'https://docs.digitalocean.com/products/databases/postgresql/how-to/connect/',
		overrides: {
			connectionStringPlaceholder: 'postgresql://doadmin:<password>@<cluster>-do-user-<id>-0.db.ondigitalocean.com:25060/defaultdb?sslmode=require',
			defaultPort: 25_060,
		},
	},
	{
		id: 'render-postgres',
		name: '$t:connectors.render-postgres.name',
		description: '$t:connectors.render-postgres.description',
		icon: 'simple-icons:render',
		iconBackground: '#46E3B7',
		iconForeground: '#1C1C1C',
		keywords: ['render', 'render.com', 'render postgres', 'render database'],
		availableSince: '2026-06-16',
		docsUrl: 'https://render.com/docs/postgresql-creating-connecting',
		overrides: {
			connectionStringPlaceholder: 'postgres://<user>:<password>@<host>.render.com:5432/<dbname>',
		},
	},
	{
		id: 'railway-postgres',
		name: '$t:connectors.railway-postgres.name',
		description: '$t:connectors.railway-postgres.description',
		icon: 'simple-icons:railway',
		iconBackground: '#0B0D0E',
		iconForeground: '#FFFFFF',
		keywords: ['railway', 'railway.app', 'railway postgres', 'railway database'],
		availableSince: '2026-06-16',
		docsUrl: 'https://docs.railway.com/databases/postgresql',
		overrides: {
			connectionStringPlaceholder: 'postgresql://<user>:<password>@<host>.railway.internal:5432/<dbname>',
		},
		notices: [
			{ color: 'neutral', title: '$t:connectors.railway-postgres.setup_notice' },
		],
	},
	{
		id: 'aws-rds-postgres',
		name: '$t:connectors.aws-rds-postgres.name',
		description: '$t:connectors.aws-rds-postgres.description',
		icon: 'simple-icons:amazonrds',
		iconBackground: '#527FFF',
		iconForeground: '#FFFFFF',
		keywords: ['aws', 'amazon', 'rds', 'postgresql', 'postgres', 'relational database service', 'cloud'],
		availableSince: '2026-06-16',
		docsUrl: 'https://docs.aws.amazon.com/AmazonRDS/latest/UserGuide/USER_ConnectToPostgreSQLInstance.html',
		overrides: {
			connectionStringPlaceholder: 'postgresql://user:password@mydb.123456789012.us-east-1.rds.amazonaws.com:5432/dbname',
		},
		authMethods: [AuthType.Password, AuthType.AwsIam],
	},
	{
		id: 'aws-aurora-postgres',
		name: '$t:connectors.aws-aurora-postgres.name',
		description: '$t:connectors.aws-aurora-postgres.description',
		icon: 'connectors:aurora',
		iconBackground: '#527FFF',
		iconForeground: '#FFFFFF',
		keywords: ['aws', 'amazon', 'aurora', 'postgresql', 'postgres', 'aurora postgresql', 'cloud'],
		availableSince: '2026-06-16',
		docsUrl: 'https://docs.aws.amazon.com/AmazonRDS/latest/AuroraUserGuide/Aurora.Connecting.html',
		overrides: {
			connectionStringPlaceholder: 'postgresql://user:password@mycluster.cluster-123456789012.us-east-1.rds.amazonaws.com:5432/dbname',
		},
		authMethods: [AuthType.Password, AuthType.AwsIam],
	},
	{
		id: 'gcp-cloud-sql-postgres',
		name: '$t:connectors.gcp-cloud-sql-postgres.name',
		description: '$t:connectors.gcp-cloud-sql-postgres.description',
		icon: 'simple-icons:googlecloud',
		iconBackground: '#4285F4',
		iconForeground: '#FFFFFF',
		keywords: ['gcp', 'google', 'cloud sql', 'postgresql', 'postgres', 'google cloud'],
		availableSince: '2026-06-16',
		docsUrl: 'https://docs.cloud.google.com/sql/docs/postgres/connect-overview',
		overrides: {
			connectionStringPlaceholder: 'postgresql://postgres:password@203.0.113.1:5432/mydb?sslmode=require',
		},
		notices: [
			{ color: 'neutral', title: '$t:connectors.gcp-cloud-sql-postgres.setup_notice' },
		],
	},
	{
		id: 'azure-postgres',
		name: '$t:connectors.azure-postgres.name',
		description: '$t:connectors.azure-postgres.description',
		icon: 'connectors:azure',
		iconBackground: '#0078D4',
		iconForeground: '#FFFFFF',
		keywords: ['azure', 'microsoft', 'postgresql', 'postgres', 'flexible server'],
		availableSince: '2026-06-16',
		docsUrl: 'https://learn.microsoft.com/en-us/azure/postgresql/flexible-server/connect-python',
		overrides: {
			connectionStringPlaceholder: 'postgresql://mylogin:password@mydemoserver.postgres.database.azure.com:5432/postgres?sslmode=require',
		},
		notices: [
			{ color: 'neutral', title: '$t:connectors.azure-postgres.setup_notice' },
		],
	},
];

export const postgresPresets = brands.map((brand) => createBrandedConnector(postgres, brand));
